import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { find, includes, map, propEq } from "ramda";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";

import { notBlank } from "../../util/ext";
import { useMediaTable } from "../../creator-application/common/use-mediaTable";
import { Box, TableFooter } from "@material-ui/core";
import { DateTime } from "luxon";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Tooltip from "@material-ui/core/Tooltip";
import { Link, useRouteMatch } from "react-router-dom";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  btnRoot: {
    lineHeight: 1.43,
  },

  btnLabel: {
    flexDirection: "column",
    alignItems: "flex-start",
  },

  link: {
    color: theme.palette.primary.brandPrimary,
  },

  tableContainer: {
    height: "calc(100vh - 350px)",
    overflow: "auto",
  },

  cell: {
    minWidth: 200,
    maxWidth: 600,
  },

  countCell: {
    minWidth: 50,
    maxWidth: 60,
  },

  typeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const MetadataFileDiscoveryTable = props => {
  const {
    metadataFileDiscoveryList,
    total = 0,
    contentProviderList,
    contentProviderEndpointList,
    page,
    onPageChange,
    onSelectChange,
    orderBy = "eventTimestamp",
    order = "asc",
    onSortChange,
    onScrollVisible,
  } = props;

  const classes = useStyles();
  const { url } = useRouteMatch();

  const {
    state,
    handleRequestSort,
    handleChangePage,
    handleRowClick,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
    },
  );

  const findContentProviderName = id => {
    const provider = find(propEq("contentProviderId", id), contentProviderList);
    return notBlank(provider) ? provider.name : "...";
  };

  const findContentProviderEndpointName = id => {
    const providerEndpoint = find(
      propEq("contentProviderEndpointId", id),
      contentProviderEndpointList,
    );
    return notBlank(providerEndpoint) ? providerEndpoint.name : "...";
  };

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer className={classes.tableContainer} ref={tableContainerEl}>
        <Table stickyHeader aria-label="Metadata File Discovery">
          <TableHead>
            <TableRow>
              <TableCell
                key="eventTimestamp"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "eventTimestamp"}
                  direction={order}
                  onClick={() => handleRequestSort("eventTimestamp")}
                >
                  Timestamp
                  {orderBy === "eventTimestamp" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="jobId" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "jobId"}
                  direction={order}
                  onClick={() => handleRequestSort("jobId")}
                >
                  Job Id
                  {orderBy === "jobId" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="contentProviderId"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "contentProviderId"}
                  direction={order}
                  onClick={() => handleRequestSort("contentProviderId")}
                >
                  Content Provider
                  {orderBy === "contentProviderId" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="metadataFileLocation"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "metadataFileLocation"}
                  direction={order}
                  onClick={() => handleRequestSort("metadataFileLocation")}
                >
                  Metadata File Location
                  {orderBy === "metadataFileLocation" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="errorCount"
                align="center"
                className={classes.countCell}
              >
                Error Count
              </TableCell>

              <TableCell
                key="mediaCount"
                align="center"
                className={classes.countCell}
              >
                Media Count
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {map(
              mfd => (
                <TableRow
                  onClick={() => handleRowClick(mfd)}
                  hover
                  tabIndex={-1}
                  key={
                    mfd.jobId + DateTime.fromISO(mfd.eventTimestamp).toMillis()
                  }
                  selected={includes(mfd.jobId, state.selected)}
                >
                  <TableCell
                    key={"eventTimestamp:" + mfd.jobId}
                    align="left"
                    className={classes.cell}
                  >
                    {DateTime.fromISO(mfd.eventTimestamp)
                      .toUTC()
                      .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                  </TableCell>

                  <TableCell
                    key={"jobId:" + mfd.jobId}
                    align="left"
                    className={classes.cell}
                  >
                    <Tooltip title="View Event">
                      <Link className={classes.link} to={`${url}/${mfd.jobId}`}>
                        {mfd.jobId}
                      </Link>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    key={"contentProviderId:" + mfd.jobId}
                    align="left"
                    className={classes.cell}
                  >
                    {notBlank(mfd.contentProviderId) && (
                      <Box>
                        <Box>
                          {findContentProviderName(mfd.contentProviderId)}
                        </Box>
                        <Box>
                          {findContentProviderEndpointName(
                            mfd.contentProviderEndpointId,
                          )}
                        </Box>
                        <Box fontSize="1.2rem">
                          ({mfd.contentProviderEndpointId})
                        </Box>
                      </Box>
                    )}
                  </TableCell>

                  <TableCell
                    key={"metadataFileLocation:" + mfd.jobId}
                    align="left"
                    className={classes.cell}
                  >
                    {mfd.metadataFileLocation}
                  </TableCell>

                  <TableCell
                    key={"errorCount:" + mfd.jobId}
                    align="center"
                    className={classes.countCell}
                  >
                    {mfd.errorCount}
                  </TableCell>

                  <TableCell
                    key={"mediaCount:" + mfd.jobId}
                    align="center"
                    className={classes.countCell}
                  >
                    {mfd.mediaCount}
                  </TableCell>
                </TableRow>
              ),
              metadataFileDiscoveryList,
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                classes={{
                  root: classes.pagination,
                }}
                rowsPerPageOptions={[25]}
                colSpan={6}
                count={total}
                rowsPerPage={25}
                page={page}
                onChangePage={(event, page) => handleChangePage(page)}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default MetadataFileDiscoveryTable;
